/**
 * Unified Activity Feed Timeline.
 *
 * Renders a chronological audit trail for a lead / student / application
 * (notes, messages, follow-ups, status changes, documents, payments).
 *
 * Backed by `GET /api/admin/activity-feed/{entity_type}/{entity_id}`.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/errors';
import {
  Loader2,
  RefreshCw,
  StickyNote,
  MessageSquare,
  Calendar,
  CheckCircle2,
  FileText,
  DollarSign,
  GraduationCap,
  Settings,
  Clock,
} from 'lucide-react';

const KIND_META = {
  note:        { icon: StickyNote,    color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  message:     { icon: MessageSquare, color: 'text-blue-600',   bg: 'bg-blue-100 dark:bg-blue-900/30' },
  follow_up:   { icon: Calendar,      color: 'text-amber-600',  bg: 'bg-amber-100 dark:bg-amber-900/30' },
  status:      { icon: CheckCircle2,  color: 'text-teal-600',   bg: 'bg-teal-100 dark:bg-teal-900/30' },
  document:    { icon: FileText,      color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  payment:     { icon: DollarSign,    color: 'text-emerald-600',bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  application: { icon: GraduationCap, color: 'text-rose-600',   bg: 'bg-rose-100 dark:bg-rose-900/30' },
  system:      { icon: Settings,      color: 'text-gray-500',   bg: 'bg-gray-100 dark:bg-slate-700' },
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function ActivityFeedTimeline({ entityType, entityId, maxHeight = '24rem' }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/activity-feed/${entityType}/${entityId}`);
      setEntries(res.data?.entries || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load activity feed'));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="activity-feed-timeline">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <Clock className="w-4 h-4 text-teal-600" />
            Activity Timeline
            {!loading && (
              <Badge variant="outline" className="ml-1">{entries.length}</Badge>
            )}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Chronological audit log of every note, status change, document, payment and communication.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="activity-feed-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">
          {error}
        </div>
      )}

      {loading && entries.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading activity…
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-400 italic bg-gray-50 dark:bg-slate-700/30 rounded-lg">
          No activity recorded yet.
        </div>
      ) : (
        <div
          className="space-y-2 overflow-y-auto pr-1"
          style={{ maxHeight }}
          data-testid="activity-feed-entries"
        >
          {entries.map((e, idx) => {
            const meta = KIND_META[e.kind] || KIND_META.system;
            const Icon = meta.icon;
            return (
              <div
                key={`${e.kind}-${idx}-${e.timestamp}`}
                className="flex gap-3 p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg hover:shadow-sm transition-shadow"
                data-testid={`activity-entry-${idx}`}
              >
                <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${meta.bg}`}>
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-800 dark:text-white">{e.action}</p>
                    <span className="text-[11px] text-gray-400 whitespace-nowrap" title={e.timestamp}>
                      {formatWhen(e.timestamp)}
                    </span>
                  </div>
                  {e.content && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap mt-0.5">
                      {e.content}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">by {e.actor || 'System'}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
